/**
 * useWallet — GET /api/v2/wallets/:id (Einzelabruf).
 *
 * Aufgabe wallets-public-2026-09: Die Wallet-Detailseite ist seit 09.2026
 * wieder öffentlich (v1-Verhalten, siehe wallets_controller.rb#show). Vorher
 * lud die Route die GESAMTE Wallet-Liste (useWallets, GET /api/v2/wallets)
 * und suchte die passende Wallet heraus — das funktioniert nur für die
 * eigenen Wallets des eingeloggten Users (`current_user.all_wallets` in der
 * Liste) und liefert für Anonyme sowie für fremde Wallets nichts. Dieser
 * Hook ruft stattdessen den Einzelendpunkt auf, der global findet und dabei
 * `name`/`owned` für Nicht-Besitzer serverseitig auf null setzt.
 *
 * GET /api/v2/wallets/{id} ist (noch) nicht im OpenAPI-Kontrakt (nur PATCH/
 * DELETE sind dort erfasst) — Muster wie addOwnedWallet/fetchWatchedWallets
 * in api/walletApi.ts: getypter apiClient, aber über `as any` gecastet statt
 * eines rohen fetch() (Lint-Regel in features/ verbietet rohes fetch).
 *
 * Code-Review-Fix: lief zunächst über ein direktes `useQuery` statt über den
 * gemeinsamen `useApi`-Wrapper (lib/use-api.ts), den jeder Schwester-Hook
 * (useHotspotDetail, useWallets, useCityDetail) nutzt. Damit fehlte das dort
 * zentral gepflegte F015-Retry/Backoff-Verhalten (BE-Warmup-Race) — jetzt
 * wieder einheitlich.
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const client = apiClient as any

export type WalletDetailData = {
  id: number
  name: string | null
  owned: boolean | null
  is_owner: boolean
  hotspot_count: number
  address: string
  sol_addr: string
  balance: number
  balance_iot: number
  balance_mobile: number
}

type UseWalletResult = {
  wallet: WalletDetailData | undefined
  isLoading: boolean
  isError: boolean
}

export function useWallet(walletId: number | string): UseWalletResult {
  const id = Number(walletId)
  const enabled = Number.isFinite(id) && id > 0

  const query = useApi<WalletDetailData>(
    ['wallet', String(id), 'detail'],
    async () => {
      const result = await client.GET('/api/v2/wallets/{id}', {
        params: { path: { id } },
      })
      if (result.error) return { error: result.error }
      const envelope = result.data as { data?: WalletDetailData } | undefined
      return { data: envelope?.data }
    },
    { enabled },
  )

  return {
    wallet: query.data,
    isLoading: enabled && query.isLoading,
    isError: query.isError,
  }
}
